import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft, Clock, GitBranch, Layers, Activity, RefreshCw, MessageSquare,
  ArrowUpRight, Github, ExternalLink, Share2, Check,
} from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";
import { api, API } from "../lib/api";

const signIn = () => {
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/dashboard")}`;
};
const signInGithub = () => { window.location.href = `${API}/auth/github/login`; };

const SEV = {
  critical: "border-primary text-primary",
  high: "border-accent text-accent",
  medium: "border-[hsl(202,46%,60%)] text-[hsl(202,46%,70%)]",
  low: "border-background/30 text-background/50",
};

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-card border-2 border-foreground shadow-brutal p-5">
      <Icon size={20} className="text-primary mb-3" />
      <div className="font-display font-black tracking-tighter text-3xl sm:text-4xl tabular-nums">{value}</div>
      <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
      {sub && <div className="font-mono text-[11px] text-muted-foreground/70 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function FrictionWall() {
  const repo = new URLSearchParams(window.location.search).get("repo");
  const [data, setData] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await api.get(`/public/friction-wall${repo ? `?repo=${encodeURIComponent(repo)}` : ""}`);
      setData(r.data);
      setUpdatedAt(new Date());
    } catch (e) { /* ignore */ } finally { setRefreshing(false); }
  }, [repo]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  // Dynamic title + OG meta for shareable links
  useEffect(() => {
    const title = repo ? `${repo} — friction intercepted live · Patchwire` : "The Friction Wall · Patchwire";
    document.title = title;
    const ogImg = `${API}/public/og${repo ? `?repo=${encodeURIComponent(repo)}` : ""}`;
    const set = (prop, val, attr = "property") => {
      let el = document.head.querySelector(`meta[${attr}="${prop}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, prop); document.head.appendChild(el); }
      el.setAttribute("content", val);
    };
    const desc = repo
      ? `Real developer friction on ${repo}, intercepted and scored by Patchwire.`
      : "Real, public developer friction intercepted live from open-source repositories.";
    set("og:title", title); set("og:description", desc); set("og:image", ogImg);
    set("og:url", window.location.href); set("twitter:card", "summary_large_image", "name");
    set("twitter:image", ogImg, "name");
  }, [repo]);

  const share = async () => {
    const url = window.location.href;
    const shareData = { title: "Patchwire Friction Wall", text: repo ? `Live developer friction on ${repo}` : "Live developer friction", url };
    try {
      if (navigator.share) { await navigator.share(shareData); return; }
    } catch (e) { /* fall through to copy */ }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true); toast.success("Share link copied"); setTimeout(() => setCopied(false), 2000);
    } catch (e) { toast.error("Could not copy link"); }
  };

  const signals = data?.signals || [];

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <nav className="sticky top-0 z-40 bg-background/85 backdrop-blur border-b-2 border-foreground">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <a href="/" data-testid="wall-back-link" className="font-mono text-xs flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft size={14} /> back
            </a>
            <Logo />
          </div>
          <div className="flex items-center gap-3">
            <button onClick={share} data-testid="wall-share-btn" className="btn btn-outline btn-sm">
              {copied ? <Check size={13} /> : <Share2 size={13} />} Share
            </button>
            <div className="hidden sm:flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
              <span className="w-2 h-2 bg-primary live-dot" /> live · 15s
            </div>
          </div>
        </div>
      </nav>

      {/* Hero band */}
      <header className="relative overflow-hidden border-b-2 border-foreground">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="relative max-w-7xl mx-auto px-5 pt-14 pb-12">
          <div className="inline-flex items-center gap-2 border-2 border-foreground bg-card px-3 py-1.5 font-mono text-xs uppercase tracking-wide mb-6">
            <Activity size={13} className="text-primary" /> {repo ? `Friction Wall · ${repo}` : "The Friction Wall"}
          </div>
          {repo ? (
            <h1 className="font-display font-black tracking-tighter leading-[0.92] text-4xl sm:text-5xl lg:text-6xl max-w-4xl break-words">
              <span className="text-primary">{repo}</span> — friction intercepted live.
            </h1>
          ) : (
            <h1 className="font-display font-black tracking-tighter leading-[0.92] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
              Real developer friction, <span className="text-primary">intercepted live.</span>
            </h1>
          )}
          <p className="mt-5 font-mono text-sm text-muted-foreground max-w-xl leading-relaxed">
            Every card below is a real, public distress signal Patchwire pulled from an open-source
            repository and scored — before a support ticket was ever filed.
          </p>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-9">
            <Stat icon={Clock} label="Dev-hours lost" value={`${(data?.developer_hours_lost ?? 0).toLocaleString()}h`} sub="estimated" />
            <Stat icon={Activity} label="Signals intercepted" value={(data?.total_signals ?? 0).toLocaleString()} />
            <Stat icon={GitBranch} label={repo ? "Repository" : "Repos watched"} value={repo ? "1" : (data?.repos_watched ?? 0).toLocaleString()} />
            <Stat icon={Layers} label="Friction categories" value={(data?.categories ?? 0).toLocaleString()} />
          </div>
        </div>
      </header>

      {/* Live wall (dark) */}
      <section className="bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-5 py-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-mono text-xs uppercase tracking-widest flex items-center gap-2">
              <span className="w-2 h-2 bg-primary live-dot" /> friction.feed · top {signals.length} by score
            </h2>
            <button onClick={load} data-testid="wall-refresh-btn"
              className="font-mono text-[11px] uppercase tracking-widest flex items-center gap-1.5 border border-background/30 px-2.5 py-1.5 hover:border-background transition-colors">
              <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
              {updatedAt ? `synced ${updatedAt.toLocaleTimeString()}` : "sync"}
            </button>
          </div>

          {signals.length === 0 ? (
            <div className="border-2 border-dashed border-background/25 p-12 text-center font-mono text-sm text-background/50">
              {data ? "No signals intercepted yet — be the first to point Patchwire at a repository." : "Streaming telemetry…"}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {signals.map((s, i) => (
                <motion.a
                  key={`${s.repo_full_name}-${s.issue_number}`}
                  href={s.html_url} target="_blank" rel="noreferrer"
                  data-testid={`wall-signal-${i}`}
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: Math.min(i * 0.015, 0.4) }}
                  className="group block border border-background/20 bg-background/[0.04] p-4 hover:bg-background/[0.08] hover:border-background/40 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-[11px] text-accent truncate">{s.error_category}</span>
                    <span className={`font-mono text-[9px] uppercase tracking-wide px-1.5 py-0.5 border ${SEV[s.severity] || SEV.low}`}>
                      {s.severity}
                    </span>
                  </div>
                  <div className="font-mono text-sm text-background/90 line-clamp-2 leading-snug min-h-[2.5rem]">{s.title}</div>
                  <div className="mt-3 flex items-center justify-between font-mono text-[11px] text-background/45">
                    <span className="truncate max-w-[55%]">{s.repo_full_name}#{s.issue_number}</span>
                    <span className="flex items-center gap-3 shrink-0">
                      <span className="flex items-center gap-1 text-primary"><Clock size={11} /> {s.estimated_hours_lost}h</span>
                      <span className="text-accent">f{s.friction_score}</span>
                      <ExternalLink size={11} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    </span>
                  </div>
                </motion.a>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 py-20 text-center">
        <h2 className="font-display font-black tracking-tighter text-3xl sm:text-5xl max-w-2xl mx-auto leading-[0.95]">
          See your own repository on the wall.
        </h2>
        <p className="font-mono text-sm text-muted-foreground mt-5 max-w-lg mx-auto">
          Point Patchwire at a repo and watch its friction surface in seconds — then ship the fix as a Pull Request.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <button onClick={signIn} data-testid="wall-cta-google" className="btn btn-primary btn-lg group">
            Continue with Google
            <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </button>
          <button onClick={signInGithub} data-testid="wall-cta-github" className="btn btn-dark btn-lg">
            <Github size={18} /> Continue with GitHub
          </button>
        </div>
      </section>

      <footer className="border-t-2 border-foreground bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-5 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo className="text-background" />
          <p className="font-mono text-xs text-background/60">© 2026 Patchwire · Live developer telemetry.</p>
        </div>
      </footer>
    </div>
  );
}
